import './Monetization.sass';

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';

import {
  Block,
  Button,
  Form,
  FormItem,
  Header,
  InfoBlock,
  Placeholder,
  ScrollView,
} from '$uikit';
import { monetizationActions, monetizationSelector } from '$store/monetization';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { Loader, Truncate, UnitName, UnitPhoto } from '$shared/components';
import Input from '$uikit/Input/Input';
import { getLinks } from '$shared/components/Linkify/Linkify';
import { toastActions } from '$store/toast';
import { IcCopy28, IcSuccess72, IcWait72 } from '$assets';
import { getLanguage } from '$translation';
import { useHandlePhotoSelectCallback } from '$core/Profile/Profile';
import { showModal } from '$navigation/helper';
import { contentPrioritySelector } from '$store/main';
import { ContentPriorityType } from '$store/main/interface';
import { CONTENT_PRIORITY, PERSONAL_SUBSCRIPTION_INFO } from '$shared/constants/pages';
import { Link } from 'react-router5';

export const Monetization: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { isLoading, isFailed, status, sumsubToken, shareUrl } =
    useSelector(monetizationSelector);
  const [links, setLinks] = useState<{ [index: string]: string }>({});
  const [about, setAbout] = useState('');
  const snsWebSdkInstance = useRef<any>();
  const handlePhotoSelect = useHandlePhotoSelectCallback();
  const user = useCurrentUserInfo();
  const contentPriority = useSelector(contentPrioritySelector);
  const isAdultPriority = contentPriority === ContentPriorityType.ADULT;

  useEffect(() => {
    dispatch(monetizationActions.loadMonetization());
  }, []);

  useEffect(() => {
    if (status === 'in_kyc') {
      // @ts-ignore
      snsWebSdkInstance.current = window.snsWebSdk
        .init(sumsubToken, () => {
          dispatch(monetizationActions.startKYC());
        })
        .withConf({
          lang: getLanguage(),
        })
        .withOptions({
          addViewportTag: false,
          adaptIframeHeight: false,
        })
        .on('idCheck.stepCompleted', (payload) => {
          console.log('stepCompleted', payload);
        })
        .on('idCheck.onError', (error) => {
          console.log('onError', error);
        })
        .build();

      snsWebSdkInstance.current.launch('#sumsub-websdk-container');
    }

    return () => {
      if (snsWebSdkInstance.current) {
        snsWebSdkInstance.current.destroy();
        snsWebSdkInstance.current = null;
      }
    };
  }, [status, sumsubToken]);

  const handleRetry = useCallback(() => {
    dispatch(monetizationActions.loadMonetization());
  }, []);

  const handleLinks = useCallback((field, value) => {
    setLinks((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleAbout = useCallback(({ target }) => {
    setAbout(target.value);
  }, []);

  const handleSend = useCallback(() => {
    if (!user.photo || user.photo.indexOf('ava_placeholder') > -1) {
      return dispatch(toastActions.setToastFail(t('monetization_photo_err')));
    }

    const linksRes: Record<string, string> = {};

    for (const field in links) {
      if (links[field] && links[field].length > 0) {
        linksRes[field] = links[field];
      }
    }

    if (Object.keys(linksRes).length < 2) {
      return dispatch(toastActions.setToastFail(t('monetization_links_err')));
    }

    const aboutFiltered = about.trim();

    dispatch(
      monetizationActions.sendRequest({
        contentType: isAdultPriority ? 'adult' : 'other',
        links: JSON.stringify(linksRes),
        about: aboutFiltered,
      }),
    );
  }, [links, about, t, user, isAdultPriority]);

  const handleStartKYC = useCallback(() => {
    dispatch(monetizationActions.startKYC());
  }, []);

  const handleCopyLink = useCallback(() => {
    if (shareUrl) {
      copyToClipboard(shareUrl);
      dispatch(toastActions.setToastSuccess(t('copied')));
      return;
    }
    dispatch(monetizationActions.generateLink());
  }, [shareUrl, dispatch, t]);

  const handleUploadPhoto = useCallback(
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      handlePhotoSelect(files);
    },
    [handlePhotoSelect],
  );

  function renderContent() {
    if (isFailed) {
      return (
        <Placeholder
          centered
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleRetry}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (isLoading) {
      return <Loader isWrapped />;
    }

    if (status === 'not_verified') {
      return (
        <div className="Monetization__content">
          {/*<Block>*/}
          {/*  <div className="Monetization__intro">*/}
          {/*    <div>{t('monetization_info')}</div>*/}
          {/*  </div>*/}
          {/*</Block>*/}
          <Block>
            <Form>
              {/*<FormItem>*/}
              {/*  <Link routeName={CONTENT_PRIORITY}>*/}
              {/*    <InfoBlock type="warning" arrow>*/}
              {/*      {t(*/}
              {/*        isAdultPriority*/}
              {/*          ? 'monetization_erotic_adult'*/}
              {/*          : 'monetization_media_warn',*/}
              {/*      )}*/}
              {/*    </InfoBlock>*/}
              {/*  </Link>*/}
              {/*</FormItem>*/}
              <FormItem>
                <div className="Monetization__profile">
                  <div className="Monetization__profile__photo">
                    <div className="Monetization__attachments__photo_wrap">
                      <UnitPhoto size="extra-large" unit={user} onlineIndicator={false} />
                      <input
                        key={'monetization_upload_photo_button'}
                        onChange={(e) => handleUploadPhoto(e)}
                        accept={'image/*'}
                        type="file"
                      />
                    </div>
                  </div>
                  <div className="Monetization__profile__body">
                    <div className="Monetization__profile__body__name">
                      <UnitName unit={user} />
                    </div>
                    <div className="Monetization__profile__body__buttons">
                      <Button
                        mode="secondary"
                        size="small"
                        onClick={() => showModal('edit')}
                      >
                        {t('monetization_button_edit_profile')}
                      </Button>
                      <div className="Monetization__attachments__photo_wrap">
                        <Button mode="secondary" size="small">
                          {t('monetization_button_upload_photo')}
                        </Button>
                        <input
                          key={'monetization_upload_photo_button'}
                          onChange={(e) => handleUploadPhoto(e)}
                          accept={'image/*'}
                          type="file"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </FormItem>
              <FormItem
                title={t('monetization_links')}
                description={t('monetization_links_description')}
              >
                <div>
                  <Input
                    value={links['Instagram']}
                    onChange={(e) => handleLinks('Instagram', e.target.value)}
                    placeholder="Instagram"
                  />
                </div>
                <div style={{ marginTop: 10 }}>
                  <Input
                    value={links['TikTok']}
                    onChange={(e) => handleLinks('TikTok', e.target.value)}
                    placeholder="TikTok"
                  />
                </div>
                <div style={{ marginTop: 10 }}>
                  <Input
                    value={links['OnlyFans']}
                    onChange={(e) => handleLinks('OnlyFans', e.target.value)}
                    placeholder="OnlyFans"
                  />
                </div>
                <div style={{ marginTop: 10 }}>
                  <Input
                    value={links['Other']}
                    onChange={(e) => handleLinks('Other', e.target.value)}
                    placeholder="Other"
                  />
                </div>
              </FormItem>
              <FormItem
                title={t('monetization_about')}
                description={
                  <Truncate
                    findLinks={false}
                    text={t('monetization_about_description')}
                  />
                }
              >
                <Input multiline value={about} onChange={handleAbout} />
              </FormItem>
              <FormItem>
                <Button onClick={handleSend}>{t('send_button')}</Button>
              </FormItem>
            </Form>
          </Block>
        </div>
      );
    } else if (status === 'in_verification' || status === 'kyc_approved') {
      return (
        <Placeholder
          centered
          title={t('monetization_in_verification_title')}
          icon={<IcWait72 color="var(--tint-gold)" />}
        >
          {t('monetization_in_verification_caption')}
        </Placeholder>
      );
    } else if (status === 'rejected') {
      return (
        <Placeholder centered title={t('monetization_rejected_title')}>
          {t('monetization_rejected_caption')}
        </Placeholder>
      );
    } else if (status === 'ready_to_kyc') {
      return (
        <Placeholder
          centered
          title={t('monetization_ready_to_kyc_title')}
          icon={<IcSuccess72 />}
          actions={
            <Button size="small" onClick={handleStartKYC}>
              {t('monetization_ready_to_kyc_btn')}
            </Button>
          }
        >
          {t('monetization_ready_to_kyc_caption')}
        </Placeholder>
      );
    } else if (status === 'verified') {
      return (
        <Placeholder
          centered
          title={t('monetization_verified_title')}
          icon={<IcSuccess72 />}
        >
          {t('monetization_verified_caption')}
        </Placeholder>
      );
    }
  }

  return (
    <>
      <Header
        showBackButton
        rightIcons={[
          status === 'in_kyc'
            ? {
                icon: <IcCopy28 />,
                onClick: handleCopyLink,
              }
            : null,
        ]}
      >
        {t('monetization_title')}
      </Header>
      <ScrollView>
        {renderContent()}
        {status === 'in_kyc' && (
          <div id="sumsub-websdk-container" className="Monetization__frame_wrap" />
        )}
      </ScrollView>
    </>
  );
};
